import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { MockupJob } from '@eip/engine';
import { useStore } from '../state/store';
import { prepareSceneForExport, prepareTemplateForExport } from '../app/session';
import { mockupReadiness } from '../state/mockup';
import { callback, engine } from '../worker/client';
import type { FilmInfo } from '../worker/protocol';
import { downloadBlob, formatExportError } from '../lib/download';
import { exportFileName } from '../lib/format';
import { buildReport, reportJson, type ReportMockup } from '../lib/report';
import { reportHtml } from '../lib/report-html';
import { exportAll, isBatchReady, type SkippedDoc } from '../lib/batch';
import { exportFilmsZip, hasBuiltSeparation } from '../lib/films';
import { exportColourways, exportFrontBack, exportMockup, mockupPlans, mockupReport, reportThumbs, type MockupDeps } from '../lib/mockups';

const SKIPPED_NOTE_MS = 10_000;
/** The proof is a reference print, not a film: 2048 px on its long edge is plenty. */
const PROOF_MAX = 2048;

/** `engine().exportFilms` with its per-film callback proxied for the worker. */
const exportFilmsVia = (id: string, generation: number, onFilm: (f: FilmInfo) => void) => engine().exportFilms(id, generation, callback(onFilm));
/** Module-scope, so a re-render does not hand the export helpers a new object
 *  every frame; `engine()` is resolved per call, as everywhere else here. */
const mockupApi: MockupDeps = {
  mockupExport: (id, job, format) => engine().mockupExport(id, job, format),
  mockupSheet: (id, jobs, labels, format, tileW, docIds) => engine().mockupSheet(id, jobs, labels, format, tileW, docIds),
  // An export renders blanks the canvas never asked for; the worker holds only
  // what the mockup runner has prepared.
  ensureTemplate: prepareTemplateForExport,
  // And the backdrop scene the job names, for the same reason: its pixels are a Blob in IndexedDB
  // until something decodes them, and an export must not race that.
  ensureScene: prepareSceneForExport,
};
const thumbApi = {
  mockupThumb: (id: string, job: MockupJob, maxDim: number) => engine().mockupThumb(id, job, maxDim),
  ensureTemplate: prepareTemplateForExport,
  ensureScene: prepareSceneForExport,
};

/** One button in the export bar (or, for `primary`, in the panel's footer). */
export interface ExportAction {
  key: string;
  label: string;
  onClick: () => void;
  disabled: boolean;
  title?: string;
  /** The button's chrome; `.btn-secondary` unless a button is deliberately plain. */
  className?: string;
}
interface Exports {
  /** The mode's one obvious export — the `.btn-primary`, pinned in the panel footer. */
  primary: ExportAction;
  /** Everything else: the two tickets, the mode's other exports, and the batch. */
  secondaries: ExportAction[];
  skipped: SkippedDoc[] | null;
  error: string | null;
}
/**
 * Why a context at all: the mode's primary export now lives in the panel's
 * pinned footer while the rest of the bar stays at the bottom of the window,
 * and the two are in different corners of the grid. They still have to share
 * ONE busy flag — the worker encodes one file at a time by design — so the
 * state cannot be duplicated per component. The provider holds it; both halves
 * read it.
 */
const ExportsContext = createContext<Exports | null>(null);

function useExports(): Exports {
  const api = useContext(ExportsContext);
  if (!api) throw new Error('ExportBar/ExportPrimary used outside ExportsProvider');
  return api;
}

/**
 * Every export the app can run, and the one-encode-at-a-time gate they share.
 * Renders no chrome of its own: `children` is the whole app, handed straight
 * through, so the provider's own state changes (a progress count ticking) never
 * re-render the layout — React bails out on an unchanged `children` element.
 */
export function ExportsProvider({ children }: { children: ReactNode }) {
  const doc = useStore((s) => s.docs.find((d) => d.id === s.activeId));
  const docs = useStore((s) => s.docs);
  const settings = useStore((s) => s.settings);
  const steps = useStore((s) => s.steps);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Batch-in-flight lives in the store, not in this component: it is the
  // app-wide "one encode at a time" fact, and other views can read it.
  const zipBusy = useStore((s) => s.batchBusy);
  const setBatchBusy = useStore((s) => s.setBatchBusy);
  const [zipProgress, setZipProgress] = useState('');
  /** "2/7" while a films export streams; null when none is running. */
  const [filmProgress, setFilmProgress] = useState<string | null>(null);
  /** "2/4" while a mockup export (sheet, pair, or the report's thumbnails) runs. */
  const [mockupProgress, setMockupProgress] = useState<string | null>(null);
  const appMode = useStore((s) => s.appMode);
  const sepMode = appMode === 'sep';
  const mockupMode = appMode === 'mockup';
  const templates = useStore((s) => s.templates);
  const templateStatus = useStore((s) => s.templateStatus);
  const scenes = useStore((s) => s.scenes);
  const mockupDefaults = useStore((s) => s.mockupDefaults);
  const [skipped, setSkipped] = useState<SkippedDoc[] | null>(null);
  const skipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { setError(null); }, [doc?.id]);
  useEffect(() => () => { if (skipTimer.current) clearTimeout(skipTimer.current); }, []);
  // `!inspecting` as well as `!running`: a report (or a ZIP) taken while the
  // inspector is still streaming would carry a partial checks list and a
  // summary computed over it — "pass" over 3 of 9 checks. `FileList` already
  // gates its status pill the same way.
  const ready = !!doc?.run && !doc.running && !doc.inspecting;
  // The batch button is a whole-document-list action: it stays enabled while
  // one doc is ready even if another is still loading, but not while any doc
  // is mid-run or mid-inspection — the docs list, each run's output and each
  // doc's checks could still change under it.
  const canZip = docs.some(isBatchReady) && !docs.some((d) => d.running || d.inspecting);
  // The films and proof buttons need PLATES built from the run on screen, not
  // just a prepared file: both are rendered from the build the worker is still
  // holding, and a build from an earlier run is stale (the rebuild is debounced).
  // Same predicate the batch skips docs with, so the bar and the archive agree.
  const sepReady = !!doc && hasBuiltSeparation(doc);
  // The same predicate the panel's status line reads: a document whose blank is
  // still preparing, or whose preview is from an older DTF run, has nothing
  // current to export. The batch's own skip rule (`hasMockup`) is looser on
  // purpose — it re-reads each doc live and lets the worker refuse.
  const mockupReady = !!doc && mockupReadiness(doc, doc.mockup ? templateStatus[doc.mockup.job.templateId] : undefined) === 'ok';
  const colorCount = doc?.mockup?.colors.length ?? 0;
  const backId = doc?.mockup?.backDocId ?? null;
  const backDoc = backId ? docs.find((d) => d.id === backId) : undefined;
  // The blank the PAIR renders the back design into — the style's back template,
  // not the front blank that document is previewed on. Read off the session's
  // derived `backJob` (the same one the viewer's Back view shows and the export
  // prints) rather than derived again here: its status is what gates the button,
  // and the session prepares it whenever a back design is bound.
  const backJob = doc?.mockup?.backJob;
  const backBlank = backJob ? templates.find((t) => t.template.id === backJob.templateId)?.template : undefined;
  const backReady = !!backDoc?.mockup && !!backBlank && templateStatus[backBlank.id]?.state === 'ready';
  const backTitle = !backDoc?.mockup ? 'Bind a back design in the mockup panel'
    : !backBlank ? 'This style has no back template'
    : !backReady ? 'Back template is still preparing'
    : undefined;
  const formatLabel = mockupDefaults.format === 'jpeg' ? 'JPEG' : 'PNG';
  /** `"#ff0000: no recolourable blank"` (a colourway) or `"Left sleeve: no design"` (a print) as
   *  the skipped note's two halves — what was dropped, and why. */
  const asSkipped = (reason: string): SkippedDoc => {
    const at = reason.indexOf(': ');
    return at > 0 ? { name: reason.slice(0, at), reason: reason.slice(at + 2) } : { name: doc?.name ?? '', reason };
  };
  /** Clears a previous batch's note as soon as any export starts. */
  const clearSkipped = () => {
    if (skipTimer.current) { clearTimeout(skipTimer.current); skipTimer.current = null; }
    setSkipped(null);
  };
  const onExport = async () => {
    if (!doc?.run) return;
    const id = doc.id;
    setBusy(true);
    setError(null);
    clearSkipped();
    try {
      const { bytes, width, height, dpi } = await engine().exportPng(id);
      // TS 5.9's lib.dom narrows ArrayBufferView<ArrayBuffer>, so a plain Uint8Array
      // no longer structurally satisfies BlobPart without this cast.
      downloadBlob(new Blob([bytes as BlobPart], { type: 'image/png' }), exportFileName(doc.name, settings.garmentHex, width, height, dpi));
    } catch (err) {
      // The user may have switched to a different file while this export was
      // in flight: an error for the doc they left must not land on whichever
      // doc is active now.
      if (useStore.getState().activeId === id) setError(formatExportError(err));
    } finally {
      // busy is local UI state (not keyed per doc), so it's always cleared
      // regardless of which doc is active now — leaving it guarded the same
      // way as the error would strand a future re-visit of `id` with a
      // permanently disabled, stuck "Encoding…" button.
      setBusy(false);
    }
  };
  const onExportFilms = async () => {
    if (!doc?.sep) return;
    const id = doc.id;
    setBusy(true);
    setError(null);
    clearSkipped();
    // "0/N" from the first frame rather than a label that only appears once the
    // first film has rendered (seconds, at 720 dpi).
    setFilmProgress(`0/${doc.sep.plates.length}`);
    try {
      const result = await exportFilmsZip(doc, steps, settings, {
        exportFilms: exportFilmsVia,
        onProgress: (d, t) => setFilmProgress(`${d}/${t}`),
      });
      downloadBlob(result.blob, result.filename);
    } catch (err) {
      if (useStore.getState().activeId === id) setError(formatExportError(err));
    } finally {
      setBusy(false);
      setFilmProgress(null);
    }
  };
  const onProofPng = async () => {
    if (!doc) return;
    const id = doc.id;
    setBusy(true);
    setError(null);
    clearSkipped();
    try {
      const bytes = await engine().proofPng(id, PROOF_MAX);
      downloadBlob(new Blob([bytes as BlobPart], { type: 'image/png' }), baseName(doc.name) + '_proof.png');
    } catch (err) {
      if (useStore.getState().activeId === id) setError(formatExportError(err));
    } finally {
      setBusy(false);
    }
  };
  /**
   * The one place a mockup download is driven: the same busy/error discipline
   * the DTF and films buttons follow (one encode at a time, an error dropped if
   * the user has moved to another document), with the progress label cleared on
   * every exit.
   */
  const runMockup = async (initialProgress: string | null, work: (d: NonNullable<typeof doc>) => Promise<{ blob: Blob; filename: string; skipped?: string[] }>) => {
    if (!doc) return;
    const id = doc.id;
    setBusy(true);
    setError(null);
    clearSkipped();
    // After the guard, so a click with no document cannot leave a count on a
    // button for an export that never started.
    setMockupProgress(initialProgress);
    try {
      const { blob, filename, skipped: dropped } = await work(doc);
      downloadBlob(blob, filename);
      // A colour no blank of the style can wear, or a print the render could not make, is left out
      // of the file — that has to be said, or the shop counts the tiles (or the prints) and wonders.
      if (dropped?.length) {
        setSkipped(dropped.map(asSkipped));
        skipTimer.current = setTimeout(() => setSkipped(null), SKIPPED_NOTE_MS);
      }
    } catch (err) {
      if (useStore.getState().activeId === id) setError(formatExportError(err));
    } finally {
      setBusy(false);
      setMockupProgress(null);
    }
  };
  const withProgress = (): MockupDeps => ({ ...mockupApi, onProgress: (d, t) => setMockupProgress(`${d}/${t}`) });
  const onMockupExport = () => runMockup(null, (d) => exportMockup(d, templates, mockupApi, mockupDefaults.format));
  const onColourways = () => runMockup(`0/${colorCount}`, (d) => exportColourways(d, templates, withProgress(), mockupDefaults.format, mockupDefaults.sheet.tileW, mockupDefaults.sheet.label));
  const onFrontBack = () => runMockup('0/2', (d) => {
    // Re-read live rather than closing over `backDoc`: the pair is two
    // documents, and the second may have been closed since this render.
    const back = useStore.getState().docs.find((b) => b.id === d.mockup?.backDocId);
    if (!back) throw new Error('the back design is no longer open');
    return exportFrontBack(d, back, templates, withProgress(), mockupDefaults.format, mockupDefaults.sheet.tileW, mockupDefaults.sheet.label);
  });

  const onExportAll = async () => {
    // Clears any note left by a previous batch as soon as a new one starts,
    // rather than leaving a stale "Skipped: …" list up for the full 10 s.
    clearSkipped();
    const snapshot = useStore.getState().docs;
    // "0/N" from the first frame: the label used to read "Zipping …" until the
    // first PNG landed, which on a large batch is tens of seconds of no count.
    setZipProgress(`0/${snapshot.filter(isBatchReady).length}`);
    setBatchBusy(true);
    setError(null);
    try {
      const result = await exportAll(snapshot, steps, settings, {
        exportPng: (id) => engine().exportPng(id),
        // Read live, not from the snapshot: a doc that was edited or closed
        // while the batch ran must be left out rather than mislabelled.
        getDoc: (id) => useStore.getState().docs.find((d) => d.id === id),
        onProgress: (d, t) => setZipProgress(`${d}/${t}`),
        // Screen-print mode archives film packages, not DTF PNGs; a doc with no
        // built separation is skipped with a reason rather than exported the
        // other way. Mockup mode archives colourways, on the same terms.
        ...(sepMode ? { films: { exportFilms: exportFilmsVia } } : {}),
        ...(mockupMode ? { mockups: { sources: templates, deps: mockupApi, format: mockupDefaults.format, scenes, templateStatus } } : {}),

      });
      downloadBlob(result.blob, result.filename);
      if (result.skipped.length > 0) {
        setSkipped(result.skipped);
        skipTimer.current = setTimeout(() => setSkipped(null), SKIPPED_NOTE_MS);
      }
    } catch (err) {
      setError(formatExportError(err));
    } finally {
      setBatchBusy(false);
    }
  };
  const baseName = (name: string) => name.replace(/\.[^.]+$/, '');
  /** The ticket's mockup rows, in every mode: `undefined` outside mockup mode, so
   *  the DTF and screen-print tickets are byte-for-byte what they were. */
  const mockupsFor = mockupMode ? (d: typeof docs[number]): ReportMockup[] | undefined => mockupReport(d, templates, mockupDefaults.format, templateStatus, docs, scenes) : undefined;
  const onReport = async () => {
    if (!doc) return;
    const id = doc.id;
    // Outside mockup mode (and for a document with nothing renderable) the ticket
    // is built and downloaded synchronously, exactly as before.
    const plans = mockupMode && mockupReady ? mockupPlans(doc, templates, mockupDefaults.format).plans : [];
    if (plans.length === 0) {
      downloadBlob(new Blob([reportHtml(buildReport([doc], steps, settings, undefined, mockupsFor))], { type: 'text/html' }), baseName(doc.name) + '_report.html');
      return;
    }
    // Thumbnails are one worker render each, so this goes through the same
    // one-encode-at-a-time gate every other export does.
    setBusy(true);
    setError(null);
    clearSkipped();
    setMockupProgress(`0/${plans.length}`);
    // Only the thumbnails are guarded: a blank that will not prepare must not
    // cost the shop its job ticket, so the ticket goes out with the reason
    // printed where the pictures would have been.
    let thumbs: Record<string, string> | undefined;
    let thumbsError: string | undefined;
    try {
      thumbs = await reportThumbs(doc, { ...thumbApi, onProgress: (d, t) => setMockupProgress(`${d}/${t}`) }, plans);
    } catch (err) {
      thumbsError = formatExportError(err);
      // The banner belongs to whatever document is on screen NOW, exactly as it
      // does for every other export here; the reason travels inside the ticket
      // regardless, printed where the pictures would have been.
      if (useStore.getState().activeId === id) setError(thumbsError);
    } finally {
      setBusy(false);
      setMockupProgress(null);
    }
    // Downloaded whether or not `id` is still the active document. The ticket
    // was asked for, the thumbnails are rendered and it describes `doc` either
    // way — returning here (as this used to) threw the whole export away
    // silently because the user had clicked another file while it ran.
    const report = buildReport([doc], steps, settings, undefined, mockupsFor);
    downloadBlob(new Blob([reportHtml(report, { thumbs, thumbsError })], { type: 'text/html' }), baseName(doc.name) + '_report.html');
  };
  const onJson = () => {
    if (!doc) return;
    // No thumbnails: the JSON is the machine-readable ticket, and a megabyte of
    // base64 in it would serve nobody.
    const report = buildReport([doc], steps, settings, undefined, mockupsFor);
    downloadBlob(new Blob([reportJson(report)], { type: 'application/json' }), baseName(doc.name) + '_report.json');
  };
  // Every button is gated on BOTH kinds of encode: the worker does one at a
  // time by design (a 5225² export holds a ~110 MB filter buffer plus its
  // output), so a single export must not start alongside a batch or vice versa.
  const encoding = busy || zipBusy;
  /* One primary per app — the mode's own single-file export, pinned in the
     panel's footer where the shop is already looking. Everything else is a
     secondary: two equal blues made the user choose between two things they
     cannot yet tell apart. */
  const primary: ExportAction = sepMode
    ? { key: 'films', label: filmProgress ? `Film ${filmProgress}…` : 'Export films (ZIP)', onClick: onExportFilms, disabled: !sepReady || encoding }
    : mockupMode
      ? {
        key: 'mockup',
        label: mockupProgress ? `Mockup ${mockupProgress}…` : busy ? 'Encoding…' : `Mockup ${formatLabel}`,
        onClick: onMockupExport, disabled: !mockupReady || encoding,
      }
      : { key: 'png', label: busy ? 'Encoding…' : 'Export PNG', onClick: onExport, disabled: !ready || encoding };
  const secondaries: ExportAction[] = [
    { key: 'report', label: 'Report', onClick: onReport, disabled: !ready || encoding },
    { key: 'json', label: 'JSON', onClick: onJson, disabled: !ready || encoding },
    // Plain chrome, as it has always had: a proof is not one of the two tickets.
    ...(sepMode ? [{ key: 'proof', label: 'Proof PNG', onClick: onProofPng, disabled: !sepReady || encoding, className: '' }] : []),
    ...(mockupMode ? [
      {
        key: 'colourways', label: 'Colourway sheet', onClick: onColourways,
        disabled: !mockupReady || colorCount < 2 || encoding,
        title: colorCount < 2 ? 'Select at least two garment colours' : undefined,
      },
      { key: 'front-back', label: 'Front + back', onClick: onFrontBack, disabled: !mockupReady || !backReady || encoding, title: backTitle },
    ] : []),
    { key: 'zip', label: zipBusy ? `Zipping ${zipProgress}…` : 'Export all (ZIP)', onClick: onExportAll, disabled: !canZip || encoding },
  ];
  return <ExportsContext.Provider value={{ primary, secondaries, skipped, error }}>{children}</ExportsContext.Provider>;
}

/** One export button, with `.btn-secondary` chrome unless it asked to be plain. */
function ActionButton({ action, className }: { action: ExportAction; className?: string }) {
  return (
    <button
      type="button" className={className ?? action.className ?? 'btn-secondary'}
      onClick={action.onClick} disabled={action.disabled} title={action.title}
    >
      {action.label}
    </button>
  );
}

/** The bottom bar's exports: the two tickets, the mode's other exports, the batch. */
export function ExportBar() {
  const { secondaries, skipped, error } = useExports();
  return (
    <div className="exportbar row right">
      {secondaries.map((a) => <ActionButton key={a.key} action={a} />)}
      {skipped && <span className="muted skipped" role="status">Skipped: {skipped.map((s) => `${s.name} (${s.reason})`).join(', ')}</span>}
      {error && <span className="error" role="alert">Export failed: {error}</span>}
    </div>
  );
}

/** The mode's primary export, pinned in the side panel's footer. */
export function ExportPrimary() {
  const { primary } = useExports();
  return <ActionButton action={primary} className="btn-primary" />;
}
